import { hasWorkspacePermission, onCapabilityPolicy } from 'core-access';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  OrganizationContextItem,
  MaintenancePortalService,
  MaintenanceSession,
} from './maintenance-portal.service';

const SELECTED_WORKSPACE_KEY = 'apex.maintenance.selectedOrgId';

@Injectable({ providedIn: 'root' })
export class MaintenanceWorkspaceService {
  private readonly api = inject(MaintenancePortalService);
  private readonly session = signal<MaintenanceSession | null>(null);
  private readonly organizations = signal<OrganizationContextItem[]>([]);

  async resolveSession(): Promise<MaintenanceSession | null> {
    this.clearSession();

    const session = await firstValueFrom(this.api.verifySession());
    if (!session?.authenticated) {
      this.session.set(null);
      return null;
    }

    this.session.set(session);
    await this.loadOrganizationContext();
    if (!this.organizations().length) { this.clearSession(); return null; }
    return session;
  }

  /**
   * The workspace in ?orgId=, else the one last chosen in this tab, else the first. Links inside the portal do not carry
   * orgId, so the choice is remembered; otherwise staff in two workspaces would always fall back to the first one.
   */
  currentWorkspace(): OrganizationContextItem | null {
    const requested = typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('orgId');
    if (requested) {
      const match = this.organizations().find(org => org.orgId === requested) ?? null;
      if (match) this.rememberWorkspace(match.orgId);
      return match;
    }
    const remembered = this.rememberedWorkspaceId();
    return this.organizations().find(org => org.orgId === remembered) ?? this.organizations()[0] ?? null;
  }

  selectWorkspace(orgId: string): void {
    if (this.organizations().some(org => org.orgId === orgId)) this.rememberWorkspace(orgId);
  }

  allows(permission: string): boolean {
    const workspace = this.currentWorkspace();
    if (permission === 'DOCUMENT_EVIDENCE_UPLOAD' && !onCapabilityPolicy(workspace)) {
      return hasWorkspacePermission(workspace, 'MAINTENANCE_CREATE', 'MAINTENANCE_UPDATE');
    }
    return hasWorkspacePermission(workspace, permission);
  }

  currentSession(): MaintenanceSession | null {
    return this.session();
  }

  clearSession(): void {
    this.session.set(null);
    this.organizations.set([]);
  }

  visibleOrganizations(): OrganizationContextItem[] {
    return this.organizations();
  }

  isMaintenance(session: MaintenanceSession | null | undefined): boolean {
    const roles = session?.roles ?? [];
    return roles.includes('ROLE_MAINTENANCE');
  }

  displayName(): string {
    const session = this.currentSession();
    return session?.displayName?.trim()
      || [session?.firstName, session?.lastName].filter(Boolean).join(' ').trim()
      || session?.email?.trim()
      || 'Maintenance Team';
  }

  async signOut(): Promise<void> {
    await firstValueFrom(this.api.logout()).catch(() => undefined);
    this.clearSession();
    window.location.href = `${environment.maintenanceAppUrl}/login`;
  }

  private rememberedWorkspaceId(): string | null {
    try {
      return typeof window === 'undefined' ? null : window.sessionStorage.getItem(SELECTED_WORKSPACE_KEY);
    } catch {
      return null;
    }
  }

  private rememberWorkspace(orgId: string): void {
    try {
      if (typeof window !== 'undefined') window.sessionStorage.setItem(SELECTED_WORKSPACE_KEY, orgId);
    } catch {
      // Storage can be blocked; the URL still selects the workspace.
    }
  }

  private async loadOrganizationContext(): Promise<void> {
    this.organizations.set(await firstValueFrom(this.api.getOrganizationContext())
      .then((context) => (context.organizations ?? []).filter(org => hasWorkspacePermission(org, 'MAINTENANCE_VIEW') && org.portalMode !== 'TENANT' && org.portalMode !== 'OWNER_PORTAL' && org.orgRole !== 'TENANT_PORTAL' && org.orgRole !== 'LANDLORD_PORTAL'))
      .catch(() => []));
  }
}
